import { useLocalStorage, createGlobalState } from "@vueuse/core";
import { computed, ref } from "vue";
import { useFetch } from "@/core/api/common";
import { errorHandler, toServerError } from "@/core/api/common/utils";
import { globals } from "@/core/globals";
import { TabsType, unauthorizedErrorEvent, useBroadcast, userBeforeUnauthorizeEvent } from "@/shared/broadcast";
import type { AfterFetchContext } from "@vueuse/core";

type IdentityErrorType = {
  code?: string;
  description?: string;
};

type ConnectTokenResponseType = {
  expires_in?: number;
  access_token?: string;
  refresh_token?: string;
  errors?: Array<IdentityErrorType>;
  error: string;
  token_type?: string;
};

function _useAuth() {
  const broadcast = useBroadcast();

  const getTokenParams = ref<URLSearchParams>();

  let getTokenRequest: Promise<unknown> = Promise.resolve();
  const {
    data,
    execute: getToken,
    isFetching: isRefreshing,
  } = useFetch("/connect/token", {
    afterFetch: updateToken,
    onFetchError: (context) => {
      if (context.response?.status !== 400) {
        errorHandler(toServerError(context.error, context.response?.status));
      }
      return context;
    },
    immediate: false,
    updateDataOnError: true,
  })
    .post(getTokenParams, "application/x-www-form-urlencoded")
    .json<ConnectTokenResponseType>();

  const isAuthorizing = ref(false);
  const authErrors = ref<Array<IdentityErrorType> | undefined>();

  const errors = computed(() => authErrors.value ?? data.value?.errors);

  const headers = computed(() => {
    if (state.value.access_token) {
      return {
        Authorization: `${state.value.token_type} ${state.value.access_token}`,
      };
    }
    return {};
  });

  const INITIAL_STATE = Object.freeze({
    expires_at: null,
    token_type: "Bearer",
    access_token: null,
    refresh_token: null,
    auth_type: null as "password" | null,
  });

  const state = useLocalStorage<{
    expires_at: Date | string | null;
    token_type: string;
    access_token: string | null;
    refresh_token: string | null;
    auth_type: "password" | null;
  }>("auth", { ...INITIAL_STATE });

  function updateToken(context: AfterFetchContext<ConnectTokenResponseType>) {
    if (context.data) {
      const { token_type, access_token, refresh_token, expires_in } = context.data;

      if (token_type && access_token && refresh_token && expires_in) {
        setTokenType(token_type);
        setExpiresAt(expires_in);
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
      }
    }

    return context;
  }

  async function authorize(username: string, password: string): Promise<void> {
    isAuthorizing.value = true;
    authErrors.value = undefined;

    const { storeId } = globals;
    getTokenParams.value = new URLSearchParams({
      grant_type: "password",
      scope: "offline_access",
      storeId,
      username,
      password,
    });

    try {
      await (getTokenRequest = getToken(true));
      state.value.auth_type = "password";
    } catch (err) {
      if (data.value?.errors?.length) {
        authErrors.value = data.value.errors;
      } else if (data.value?.error) {
        authErrors.value = [{ code: data.value.error, description: data.value.error }];
      } else {
        authErrors.value = [{ code: "network_error", description: "Network error occurred" }];
      }
      throw err;
    } finally {
      isAuthorizing.value = false;
    }
  }

  async function externalSignInCallback(): Promise<void> {
    const params = new URLSearchParams({
      grant_type: "external_sign_in",
      scope: "offline_access",
    });

    getTokenParams.value = params;

    await (getTokenRequest = getToken(true));
  }

  async function refresh(organizationId?: string) {
    const params = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: state.value.refresh_token!,
    });

    if (organizationId) {
      params.set("organization_id", organizationId);
    }

    getTokenParams.value = params;

    try {
      if (!isRefreshing.value) {
        await (getTokenRequest = getToken(true));
      } else {
        await getTokenRequest;
      }
    } catch {
      state.value = { ...INITIAL_STATE };
      void broadcast.emit(unauthorizedErrorEvent, undefined, TabsType.CURRENT);
    }
  }

  async function unauthorize(): Promise<string | undefined> {
    await broadcast.emit(userBeforeUnauthorizeEvent, undefined, TabsType.CURRENT);

    await useFetch("/revoke/token").post();
    state.value = { ...INITIAL_STATE };

    return buildKeycloakLogoutUrl();
  }

  function buildKeycloakLogoutUrl(): string | undefined {
    const keycloakUrl = window.__APP_CONFIG__.APP_KEYCLOAK_URL;
    const keycloakRealm = window.__APP_CONFIG__.APP_KEYCLOAK_REALM;
    const clientId = window.__APP_CONFIG__.APP_KEYCLOAK_CLIENT_ID;

    if (!keycloakUrl || !keycloakRealm) {
      return undefined;
    }

    const url = new URL(`/realms/${keycloakRealm}/protocol/openid-connect/logout`, keycloakUrl);
    url.searchParams.set("client_id", clientId || "platform");
    url.searchParams.set("post_logout_redirect_uri", `${window.location.origin}/`);
    return url.href;
  }

  function isExpired() {
    if (state.value.refresh_token === null || state.value.expires_at === null) {
      return null;
    }

    return new Date(state.value.expires_at).getTime() <= Date.now();
  }

  function setRefreshToken(token: string) {
    state.value.refresh_token = token;
  }

  function setAccessToken(token: string) {
    state.value.access_token = token;
  }

  function setExpiresAt(seconds: number) {
    state.value.expires_at = new Date(Date.now() + seconds * 1000);
  }

  function setTokenType(type: string) {
    state.value.token_type = type;
  }

  return {
    headers,
    isExpired,
    errors,
    isAuthorizing,
    authorize,
    externalSignInCallback,
    refresh,
    unauthorize,

    setTokenType,
    setAccessToken,
    setExpiresAt,
  };
}

export const useAuth = createGlobalState(_useAuth);
